import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { supabase } from "../supabaseClient";
import { authenticatedFetch } from "../network/api";

export const fetchClient = createAsyncThunk(
  "clientData/fetchClient",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(
        `/api/client/fetchClientData?user_id=${userId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Check if the response is OK
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch client data");
      }

      // Parse the JSON response
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching client data:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Fetch Stream token
export const fetchToken = createAsyncThunk(
  "clientData/fetchToken",
  async (userId) => {
    const response = await fetch("/api/generateStreamToken", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ stream_id: userId }),
    });

    const { token, expiresAt, generatedAt } = await response.json();
    if (!token) {
      throw new Error("Failed to generate Stream token");
    }
    return { token, expiresAt, generatedAt };
  }
);

export const fetchVideoToken = createAsyncThunk(
  "clientData/fetchVideoToken",
  async (userId) => {
    const response = await fetch("/api/generateStreamVideoToken", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ stream_id: userId }),
    });

    const responseJson = await response.json();
    const { videoToken, expiresAt, generatedAt } = responseJson;

    if (!videoToken) {
      throw new Error("Failed to generate Stream token");
    }
    return { videoToken, expiresAt, generatedAt };
  }
);

const initialState = {
  clientData: null,
  status: "idle", // idle | loading | succeeded | failed | online
  token: null,
  videoToken: null,
  tokenExpiresAt: null,
  tokenGeneratedAt: null,
  videoTokenExpiresAt: null,
  videoTokenGeneratedAt: null,
  error: null,
};

const clientDataSlice = createSlice({
  name: "clientData",
  initialState,
  reducers: {
    // Reset the client data to initial state
    resetClientData: () => initialState,

    // Action to manually update client data
    updateClientData: (state, action) => {
      state.clientData = action.payload; // Update the client data with the payload
    },

    // Action to update the status
    updateStatus: (state, action) => {
      state.status = action.payload; // Set the status to the value passed in payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClient.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchClient.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.clientData = action.payload;
      })
      .addCase(fetchClient.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(fetchToken.fulfilled, (state, action) => {
        state.token = action.payload.token;
        state.tokenExpiresAt = action.payload.expiresAt;
        state.tokenGeneratedAt = action.payload.generatedAt;
      })
      .addCase(fetchToken.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(fetchVideoToken.fulfilled, (state, action) => {
        state.videoToken = action.payload.videoToken;
        state.videoTokenExpiresAt = action.payload.expiresAt;
        state.videoTokenGeneratedAt = action.payload.generatedAt;
      })
      .addCase(fetchVideoToken.rejected, (state, action) => {
        state.error = action.error.message;
      });
  },
});

export const { resetClientData, updateClientData, updateStatus } =
  clientDataSlice.actions;

export default clientDataSlice.reducer;
